import { ChangeEvent, Fragment, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

import {
  Button,
  CancelSubmitButtons,
  CardInputsContainer,
  CodeVerification,
  VerifyPhoneNumberModal,
} from "~/common/components";
import { getDisabledClasses } from "~/common/components/ui/FieldsetCard";
import Input from "~/common/components/ui/Input";
import {
  CODE_VERIFICATION_CHANNELS,
  CODE_VERIFICATION_SCOPES,
  COUNTRY_CODES,
  DEFAULT_CODE_LENGTH,
  INITIAL_VERIFICATION_SESSION,
} from "~/common/constants/verificationCode";
import { useCodeVerificationCooldown } from "~/common/hooks/useCodeVerificationCooldown";
import { customErrorSchema } from "~/common/types";
import { CodeVerificationSession } from "~/common/types/verificationCode";
import { useFormatHook } from "~/patients/hooks";
import { useCurrentTeamMemberContext } from "~/providers/hooks";
import { useCancelVerificationCode } from "~/providers/hooks/useCancelVerificationCode";
import { useRetryVerificationCode } from "~/providers/hooks/useRetryVerificationCode";
import { useSendVerificationCode } from "~/providers/hooks/useSendVerificationCode";
import { useUpdatePhoneNumber } from "~/providers/hooks/useUpdatePhoneNumber";
import { useVerifyCode } from "~/providers/hooks/useVerifyCode";

export const updateTeamMemberAccountAccessSchema = z.object({
  email: z.email("Please enter a valid email address"),
  phone_number: z
    .string()
    .min(1, "Mobile number required")
    .regex(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, {
      error: "Please enter mobile number with format: (123) 456-7890",
    }),
});

type TeamMemberAccountAccessForm = z.infer<
  typeof updateTeamMemberAccountAccessSchema
>;

export const AccountAccess = () => {
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const currentTeamMember = useCurrentTeamMemberContext();
  const [isEditing, setIsEditing] = useState(false);
  const { formatPhoneNumber, stripPhoneNumber } = useFormatHook();
  const [currentCode, setCurrentCode] = useState("");
  const queryClient = useQueryClient();

  const [invalidCodeErrorMessage, setInvalidCodeErrorMessage] =
    useState<string>();

  const [verificationSession, setVerificationSession] =
    useState<CodeVerificationSession>(INITIAL_VERIFICATION_SESSION);

  const { cooldown } = useCodeVerificationCooldown(
    verificationSession.canResendAt,
  );

  const currentTeamMemberPhoneNumber = currentTeamMember.phone_number?.number
    ? formatPhoneNumber(currentTeamMember.phone_number.number)
    : "";

  const {
    reset,
    register,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm<TeamMemberAccountAccessForm>({
    resolver: zodResolver(updateTeamMemberAccountAccessSchema),
    defaultValues: {
      email: currentTeamMember.email ?? "",
      phone_number: currentTeamMemberPhoneNumber,
    },
    mode: "all",
  });

  const selectedTeamMemberPhoneNumber = watch("phone_number");

  const { mutate: sendVerificationCode, isPending: isSendingCode } =
    useSendVerificationCode();
  const { mutate: verifyCode, isPending: isVerifyingCode } = useVerifyCode();
  const { mutate: cancelVerificationCode, isPending: isCancellingCode } =
    useCancelVerificationCode();
  const { mutate: retryVerificationCode, isPending: isRetryingCode } =
    useRetryVerificationCode();

  const shouldDisableModal =
    isSendingCode || isVerifyingCode || isRetryingCode || isCancellingCode;

  const { mutate: updatePhoneNumber } = useUpdatePhoneNumber();

  const onEdit = () => setIsEditing(true);

  const onCancel = () => {
    reset({
      email: currentTeamMember.email ?? "",
      phone_number: currentTeamMemberPhoneNumber,
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    const isValid = await trigger();

    if (!isValid) return;

    if (cooldown.active) {
      return toast.warning(
        `Cannot send SMS right now. Please wait ${cooldown.remainingTime} seconds before trying again.`,
      );
    }

    const phoneNumberChanged =
      selectedTeamMemberPhoneNumber !== currentTeamMemberPhoneNumber;

    if (!phoneNumberChanged) {
      return toast.error("You haven't changed your mobile number.");
    }

    setShowVerifyModal(true);

    sendVerificationCode(
      {
        channel: CODE_VERIFICATION_CHANNELS.SMS,
        scope: CODE_VERIFICATION_SCOPES.PHONE_UPDATE,
        phone_number: {
          number: stripPhoneNumber(selectedTeamMemberPhoneNumber),
          country_code: COUNTRY_CODES.US,
        },
      },
      {
        onSuccess: ({ can_resend_at, verification_token }) => {
          setVerificationSession((prev) => ({
            ...prev,
            canResendAt: new Date(can_resend_at).getTime(),
            token: verification_token,
          }));
        },
      },
    );
  };

  const handleVerify = () => {
    if (!verificationSession.token) return;

    if (currentCode.length < DEFAULT_CODE_LENGTH) {
      setInvalidCodeErrorMessage(
        `${DEFAULT_CODE_LENGTH} character code is required`,
      );
      return;
    }

    verifyCode(
      {
        verification_code: currentCode,
        verification_token: verificationSession.token,
      },
      {
        onSuccess: ({ one_use_token }) => {
          setVerificationSession(INITIAL_VERIFICATION_SESSION);
          setCurrentCode("");
          updatePhoneNumber(
            {
              verification_token: one_use_token,
              phone_number: {
                number: stripPhoneNumber(selectedTeamMemberPhoneNumber),
                country_code: COUNTRY_CODES.US,
              },
              user_id: currentTeamMember.id,
            },
            {
              onSuccess: () => {
                queryClient
                  .invalidateQueries({ queryKey: ["getCurrentTeamMember"] })
                  .catch(() => {
                    toast.error("Failed to refresh provider data.");
                  });
                setShowVerifyModal(false);
                setIsEditing(false);
                toast.success("Mobile number updated");
              },
            },
          );
        },
        onError: (error) => {
          const result = customErrorSchema.safeParse(error);
          if (result.success) {
            if (result.data.response.data.detail === "Token expired") {
              setInvalidCodeErrorMessage(
                "Code has expired, please try another one.",
              );
              setVerificationSession((prev) => ({
                ...prev,
                isExpired: true,
              }));
              return;
            }
          }
          setInvalidCodeErrorMessage(
            "This code doesn't match. Please try again.",
          );
        },
      },
    );
  };

  const handleClose = () => {
    if (shouldDisableModal) return;

    setShowVerifyModal(false);
    setInvalidCodeErrorMessage(undefined);
    setCurrentCode("");

    if (!verificationSession.token) return;

    cancelVerificationCode(
      { verification_token: verificationSession.token },
      {
        onSuccess: () => {
          setVerificationSession(INITIAL_VERIFICATION_SESSION);
        },
      },
    );
  };

  const handleChange = (code: string) => {
    setCurrentCode(code);
    setInvalidCodeErrorMessage(undefined);
  };

  const restartVerificationFlow = () => {
    if (!verificationSession.token) return;
    cancelVerificationCode(
      {
        verification_token: verificationSession.token,
      },
      {
        onSuccess: () => {
          sendVerificationCode(
            {
              phone_number: {
                number: stripPhoneNumber(selectedTeamMemberPhoneNumber),
                country_code: COUNTRY_CODES.US,
              },
              scope: CODE_VERIFICATION_SCOPES.PHONE_UPDATE,
              channel: CODE_VERIFICATION_CHANNELS.SMS,
            },
            {
              onSuccess: ({ verification_token, can_resend_at }) => {
                setVerificationSession((prev) => ({
                  ...prev,
                  token: verification_token,
                  canResendAt: new Date(can_resend_at).getTime(),
                  isExpired: false,
                }));
              },
            },
          );
        },
      },
    );
  };

  const handleRetry = () => {
    if (!verificationSession.token) return;

    if (verificationSession.isExpired) {
      restartVerificationFlow();
      return;
    }

    retryVerificationCode(
      {
        verification_token: verificationSession.token,
      },
      {
        onSuccess: ({ can_resend_at, verification_token }) => {
          setVerificationSession((prev) => ({
            ...prev,
            canResendAt: new Date(can_resend_at).getTime(),
            token: verification_token,
            isExpired: false,
          }));
        },
        onError: (error) => {
          const result = customErrorSchema.safeParse(error);
          if (result.success) {
            if (result.data.response.data.detail === "Token expired") {
              restartVerificationFlow();
              return;
            }
            toast.error(result.data.response.data.detail);
          }
        },
      },
    );
  };

  return (
    <Fragment>
      <VerifyPhoneNumberModal
        show={showVerifyModal}
        onClose={handleClose}
        onVerify={handleVerify}
        className="max-w-xl"
      >
        <CodeVerification
          value={currentCode}
          onChange={handleChange}
          onRetry={handleRetry}
          inCooldownTime={cooldown.active}
          remainingTime={cooldown.remainingTime}
          disabled={shouldDisableModal}
          error={invalidCodeErrorMessage}
          instructions={`Enter the code sent by SMS to ${selectedTeamMemberPhoneNumber}`}
        />
      </VerifyPhoneNumberModal>
      <CardInputsContainer
        title="Account access"
        containerClassName="flex-row items-center"
        contentClassName="grid gap-4 md:grid-cols-2"
        actions={
          isEditing ? (
            <CancelSubmitButtons
              containerClassName="w-auto"
              submitButtonLabel="Save"
              handleCancel={onCancel}
              handleSubmit={handleSave}
              submitButtonType="button"
            />
          ) : (
            <Button onClick={onEdit} className="bg-white" variant="outline">
              Edit
            </Button>
          )
        }
      >
        <Input
          id="email"
          label="Login email"
          disabled
          {...getDisabledClasses(!isEditing)}
          {...register("email")}
          error={errors?.email?.message}
        />
        <Input
          id="mobile"
          label="Mobile number"
          disabled={!isEditing}
          {...getDisabledClasses(!isEditing)}
          {...register("phone_number", {
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              setValue("phone_number", formatPhoneNumber(event.target.value));
            },
          })}
          error={errors?.phone_number?.message}
        />
      </CardInputsContainer>
    </Fragment>
  );
};
